import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { AiSlideOutChatGlobal } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal-global";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { ObjectId } from "bson";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntercom } from "react-use-intercom";

type SupportThread = {
  _id: string;
  updatedAt: string | Date;
  messages: Message[];
};

export type HelpChatGlobalProps = {
  onChatOpen?: () => void;
  onLoadThread?: (threadId: string, thread: SupportThread) => void;
  onRegisterTriageThreadCallback?: (
    callback: (threadId: string) => void,
  ) => void | (() => void);
};

export const HelpChatGlobal = memo(function HelpChatGlobal({
  onChatOpen,
  onLoadThread,
  onRegisterTriageThreadCallback,
}: HelpChatGlobalProps) {
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);
  const hasInitializedRef = useRef(false);
  const user = useRequiredContext(UserContext);
  const intercom = useIntercom();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { close } = useAiSlidePanel();
  const { pageContext, defaultSuggestedQuestions } =
    useContext(GlobalAgentContext);
  const suggestedQuestions =
    pageContext?.suggestedQuestions ?? defaultSuggestedQuestions;

  const refreshThreads = useCallback(() => {
    setThreadsRefreshKey((prev) => prev + 1);
  }, []);

  const handleNewTriageThread = useCallback(
    (threadId: string) => {
      setCurrentThreadId(threadId);
      refreshThreads();
    },
    [refreshThreads],
  );

  useEffect(() => {
    if (onRegisterTriageThreadCallback) {
      const cleanup = onRegisterTriageThreadCallback(handleNewTriageThread);
      if (typeof cleanup === "function") {
        return cleanup;
      }
      return undefined;
    }
    return undefined;
  }, [onRegisterTriageThreadCallback, handleNewTriageThread]);

  const threadsLoad = useLoad(async () => {
    if (!user.email) {
      return { threads: [] };
    }
    return await merchantRpcClient.getMerchantSupportAgentThreads({
      userEmail: user.email,
    });
  }, [merchantRpcClient, user.email, threadsRefreshKey]);

  const {
    clearMessages,
    clearAssets,
    setInputValue,
    stopStream,
    addMessage,
    sendMessage,
  } = useContext(AiChatContext);

  const startNewConversation = useHandler(() => {
    onChatOpen?.();
    stopStream();
    clearAssets();
    clearMessages();
    setInputValue("");
    setCurrentThreadId(undefined);
    hasInitializedRef.current = true;
  });

  const { isOpen, panelOptions, clearPanelOptions } = useAiSlidePanel();

  // When the panel opens, always start a new conversation
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      startNewConversation();
    }

    // Reset initialization flag when panel closes
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, startNewConversation]);

  // Handle prefill input when panel opens with options
  useEffect(() => {
    if (panelOptions?.prefillInput) {
      setInputValue(panelOptions.prefillInput);
      if (panelOptions.autoSubmit) {
        // Store the prefill value to avoid closure issues
        const prefillValue = panelOptions.prefillInput;
        // Use setTimeout to ensure the input value is set before sending
        const timeoutId = setTimeout(() => {
          sendMessage(prefillValue);
        }, 100);
        // Add cleanup to prevent potential race conditions
        return () => clearTimeout(timeoutId);
      }
      clearPanelOptions();
    }
    return undefined;
  }, [panelOptions, setInputValue, sendMessage, clearPanelOptions]);

  const openIntercomAction = useCallback(() => {
    close();
    intercom.showMessages();
  }, [close, intercom]);

  const messageHistoryButton = useMemo(
    () => (
      <Tooltip arrow placement="top" title="Message history with team">
        <RedoButton
          hierarchy="secondary"
          IconLeading={ClockIcon}
          onClick={openIntercomAction}
        />
      </Tooltip>
    ),
    [openIntercomAction],
  );

  const loadThreadIntoChat = useCallback(
    (threadId: string, thread: SupportThread) => {
      stopStream();
      clearAssets();
      clearMessages();
      setInputValue("");

      for (const [index, message] of thread.messages.entries()) {
        if (message.role === "tool") continue;

        const rawContent: any = (message as any).content;
        const contentToUse =
          typeof rawContent === "string" || Array.isArray(rawContent)
            ? rawContent
            : String(rawContent ?? "");

        if (typeof contentToUse === "string" && contentToUse.trim() === "") {
          continue;
        }

        addMessage({
          id: message.id ?? new ObjectId().toString() + index,
          role: message.role,
          content: contentToUse as any,
        } as any);
      }

      onLoadThread?.(threadId, thread);
      setCurrentThreadId(threadId);
    },
    [
      stopStream,
      clearAssets,
      clearMessages,
      setInputValue,
      addMessage,
      onLoadThread,
    ],
  );

  const threads = threadsLoad.value?.threads || [];

  return (
    <AiSlideOutChatGlobal
      currentThreadId={currentThreadId}
      hideClearButton
      leadingToolbarContent={messageHistoryButton}
      onClose={close || (() => {})}
      onLoadThread={loadThreadIntoChat}
      onNewConversation={startNewConversation}
      suggestedQuestions={suggestedQuestions}
      threads={threads}
    />
  );
});
